// Client-side iCal-Generation für Single-Event-Downloads — Doc 2026-05-17:
// „nur einzelne Termine in den Kalender übertragen". Kein Backend-Roundtrip,
// wir haben die Event-Daten schon im Frontend.

export interface SingleCalEvent {
  /** ISO date YYYY-MM-DD. */
  date: string;
  /** Berlin „HH:MM" — Lokal-Zeit. */
  timeBerlin: string;
  /** ET „HH:MM" — wird in Description gelegt. */
  timeEt: string;
  /** Indicator-Name z.B. „CPI YoY". */
  indicator: string;
  /** Country-Code (US/DE/EU/GB). */
  country: string;
  importance: 'low' | 'medium' | 'high';
  forecast?: string | null;
  prior?: string | null;
  actual?: string | null;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Lokal-Zeit (Berlin) → UTC-Date. */
function berlinToUtc(date: string, timeBerlin: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{1,2}:\d{2}$/.test(timeBerlin)) return null;
  // Wir lassen den Browser die Zeitzone berechnen — Berlin = Europe/Berlin.
  // Doc-Pragmatik: wir bauen "YYYY-MM-DDTHH:MM:00+02:00" für CEST, bzw +01:00 für CET.
  // Cheap: Date.parse mit lokaler Zone, dann toUTC. Der Browser des Users sollte Berlin sein.
  const iso = `${date}T${timeBerlin.length === 4 ? '0' + timeBerlin : timeBerlin}:00`;
  const ts = new Date(iso);
  return Number.isFinite(ts.getTime()) ? ts : null;
}

function fmtUtc(d: Date): string {
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

function icalEscape(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function flagEmoji(country: string): string {
  switch (country.toUpperCase()) {
    case 'US':
      return '🇺🇸';
    case 'DE':
      return '🇩🇪';
    case 'EU':
      return '🇪🇺';
    case 'GB':
      return '🇬🇧';
    default:
      return '🌐';
  }
}

function impactLabel(i: 'low' | 'medium' | 'high'): string {
  return i === 'high' ? 'HOCH' : i === 'medium' ? 'MITTEL' : 'NIEDRIG';
}

/** Baut einen Single-Event VCALENDAR-String mit RFC-5545 CRLF-Endings. */
export function buildSingleEventIcal(event: SingleCalEvent): string {
  const start = berlinToUtc(event.date, event.timeBerlin);
  if (!start) return '';
  const end = new Date(start.getTime() + 30 * 60 * 1000);
  const dtstamp = fmtUtc(new Date());
  const flag = flagEmoji(event.country);
  const summary = `${flag} ${event.indicator} · ${impactLabel(event.importance)}`;
  const desc = [
    `Land: ${event.country}`,
    `Impact: ${impactLabel(event.importance)}`,
    event.forecast ? `Prognose: ${event.forecast}` : null,
    event.prior ? `Vorher: ${event.prior}` : null,
    event.actual ? `Aktuell: ${event.actual}` : null,
    `Berlin: ${event.timeBerlin} · ET: ${event.timeEt}`,
    '',
    'EVE Dashboard: https://eve.upro-capital.com/trading/macro',
  ]
    .filter(Boolean)
    .join('\\n');
  const uid = `${event.date.replace(/-/g, '')}-${event.indicator.replace(/[^a-z0-9]/gi, '').slice(0, 24)}@eve.upro-capital.com`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//EVE Dashboard//Macro//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${fmtUtc(start)}`,
    `DTEND:${fmtUtc(end)}`,
    `SUMMARY:${icalEscape(summary)}`,
    `DESCRIPTION:${desc}`,
    'URL:https://eve.upro-capital.com/trading/macro',
    `CATEGORIES:${event.country === 'US' ? 'US Macro' : event.country === 'DE' ? 'DE Macro' : 'Macro'}`,
  ];
  if (event.importance === 'high') {
    lines.push(
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${icalEscape(`${event.indicator} in 15 min`)}`,
      'TRIGGER:-PT15M',
      'END:VALARM',
    );
  }
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}

/** Triggert Browser-Download einer .ics-Datei. */
export function downloadSingleEventIcal(event: SingleCalEvent): void {
  const ics = buildSingleEventIcal(event);
  if (!ics) return;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `eve-${event.date}-${event.indicator.replace(/[^a-z0-9]/gi, '-').toLowerCase().slice(0, 32)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Erzeugt eine Google-Calendar-„Add-Event"-URL. */
export function buildGoogleCalendarUrl(event: SingleCalEvent): string {
  const start = berlinToUtc(event.date, event.timeBerlin);
  if (!start) return 'https://calendar.google.com/calendar/r';
  const end = new Date(start.getTime() + 30 * 60 * 1000);
  const flag = flagEmoji(event.country);
  const details = [
    `Land: ${event.country}`,
    `Impact: ${impactLabel(event.importance)}`,
    event.forecast ? `Prognose: ${event.forecast}` : '',
    event.prior ? `Vorher: ${event.prior}` : '',
    `Berlin: ${event.timeBerlin} · ET: ${event.timeEt}`,
    '',
    'EVE Dashboard: https://eve.upro-capital.com/trading/macro',
  ]
    .filter(Boolean)
    .join('\n');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${flag} ${event.indicator}`,
    dates: `${fmtUtc(start)}/${fmtUtc(end)}`,
    details,
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}
